import { useState, useRef, useCallback } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import { trpc } from "./trpc";
import { loggerLink } from "@trpc/client/links/loggerLink";
import { useAuth } from "@/lib/auth";
import { observable } from "@trpc/server/observable";
import type { TRPCLink } from "@trpc/client";
import type { AppRouter } from "@template-name/trpc-types";

function authRefreshLink(opts: {
  getRefreshToken: () => string | null;
  getAccessToken: () => string | null;
  setAccessTokenRef: (token: string) => void;
  onTokens: (access: string, refresh: string) => void;
  onLogout: () => void;
  serverUrl: string;
}): TRPCLink<AppRouter> {
  let refreshPromise: Promise<boolean> | null = null;

  async function doRefresh(): Promise<boolean> {
    const refreshToken = opts.getRefreshToken();
    if (!refreshToken) return false;

    try {
      // Call tRPC mutation directly via fetch
      const res = await fetch(`${opts.serverUrl}/trpc/auth.refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ json: { refreshToken } }),
      });

      if (!res.ok) return false;

      const data = await res.json();
      // tRPC response format: { result: { data: { json: ... } } }
      // or for batch: [{ result: { data: { json: ... } } }]
      const json =
        data?.result?.data?.json ??
        data?.[0]?.result?.data?.json ??
        null;

      if (json?.accessToken && json?.refreshToken) {
        // Update ref immediately so the retry uses the new token
        opts.setAccessTokenRef(json.accessToken);
        opts.onTokens(json.accessToken, json.refreshToken);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  return () => {
    return ({ next, op }) => {
      return observable((observer) => {
        let retried = false;

        const execute = () => {
          const unsubscribe = next(op).subscribe({
            next(value) {
              observer.next(value);
            },
            error(err) {
              // Only intercept UNAUTHORIZED, only once, not on auth routes
              if (
                !retried &&
                err instanceof TRPCClientError &&
                err.data?.code === "UNAUTHORIZED" &&
                !op.path.startsWith("auth.")
              ) {
                retried = true;

                // Deduplicate concurrent refresh attempts
                if (!refreshPromise) {
                  refreshPromise = doRefresh().finally(() => {
                    refreshPromise = null;
                  });
                }

                refreshPromise.then((success) => {
                  if (success) {
                    // Retry with new token (httpBatchLink will read updated ref)
                    execute();
                  } else {
                    opts.onLogout();
                    observer.error(err);
                  }
                });
              } else {
                observer.error(err);
              }
            },
            complete() {
              observer.complete();
            },
          });

          return unsubscribe;
        };

        execute();
      });
    };
  };
}

export function TRPCReactProvider(props: { children: React.ReactNode }) {
  const { accessToken, refreshToken, setTokens, logout } = useAuth();
  const accessTokenRef = useRef(accessToken);
  const refreshTokenRef = useRef(refreshToken);
  const setTokensRef = useRef(setTokens);
  accessTokenRef.current = accessToken;
  refreshTokenRef.current = refreshToken;
  setTokensRef.current = setTokens;

  const serverUrl = import.meta.env.VITE_NESTJS_SERVER;

  const logoutRef = useRef(logout);
  logoutRef.current = logout;

  const handleLogout = useCallback(() => {
    logoutRef.current();
    window.location.href = "/login";
  }, []);

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // Don't retry on auth errors — our link handles refresh
            retry: (failureCount, error) => {
              if (
                error instanceof TRPCClientError &&
                error.data?.code === "UNAUTHORIZED"
              ) {
                return false;
              }
              return failureCount < 3;
            },
            // Refetch on window focus, but not too aggressively
            staleTime: 30_000,
          },
        },
      }),
  );

  const [trpcClient] = useState(() =>
    trpc.createClient({
      links: [
        loggerLink({ enabled: () => import.meta.env.DEV }),
        authRefreshLink({
          getRefreshToken: () => refreshTokenRef.current,
          getAccessToken: () => accessTokenRef.current,
          setAccessTokenRef: (token: string) => { accessTokenRef.current = token; },
          onTokens: (access: string, refresh: string) => { setTokensRef.current(access, refresh); },
          onLogout: handleLogout,
          serverUrl,
        }),
        httpBatchLink({
          url: `${serverUrl}/trpc`,
          headers() {
            const token = accessTokenRef.current;
            return token ? { Authorization: `Bearer ${token}` } : {};
          },
        }),
      ],
    }),
  );

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        {props.children}
      </QueryClientProvider>
    </trpc.Provider>
  );
}
